/* ===========================
   Romanian UI Labels
   Centralized labels for all enums and UI text
   =========================== */

import { ChurchStatus, MembershipType, Gender, CivilStatus, CIVIL_STATUS_UNSPECIFIED } from '@/types/member';
import { GroupCategory } from '@/types/group';
import type { VisitType } from '@/types/visit';
import type { NeedCategory, NeedStatus, NeedPriority } from '@/types/need';


/* --- Church Status --- */
export const CHURCH_STATUS_LABELS: Record<ChurchStatus, string> = {
  [ChurchStatus.ACTIVE]: 'Activ',
  [ChurchStatus.UNDER_DISCIPLINE]: 'Sub Disciplină',
  [ChurchStatus.TRANSFERRED]: 'Transferat',
  [ChurchStatus.LEFT]: 'Plecat',
  [ChurchStatus.EXCOMMUNICATED]: 'Exclus',
  [ChurchStatus.DECEASED]: 'Decedat',
};

export const CHURCH_STATUS_COLORS: Record<ChurchStatus, string> = {
  [ChurchStatus.ACTIVE]: 'success',
  [ChurchStatus.UNDER_DISCIPLINE]: 'warning',
  [ChurchStatus.TRANSFERRED]: 'medium',
  [ChurchStatus.LEFT]: 'medium',
  [ChurchStatus.EXCOMMUNICATED]: 'danger',
  [ChurchStatus.DECEASED]: 'dark',
};

/* --- Membership Type --- */
export const MEMBERSHIP_TYPE_LABELS: Record<MembershipType, string> = {
  [MembershipType.MEMBER]: 'Membru',
  [MembershipType.DEPENDENT]: 'Aparținător',
};

/* --- Gender --- */
export const GENDER_LABELS: Record<Gender, string> = {
  [Gender.MALE]: 'Masculin',
  [Gender.FEMALE]: 'Feminin',
};

/* --- Civil Status --- */
export const CIVIL_STATUS_LABELS: Record<CivilStatus, string> = {
  [CivilStatus.MARRIED]: 'Căsătorit(ă)',
  [CivilStatus.UNMARRIED]: 'Necăsătorit(ă)',
  [CivilStatus.WIDOWED]: 'Văduv(ă)',
  [CivilStatus.DIVORCED]: 'Divorțat(ă)',
  [CivilStatus.SEPARATED]: 'Separat(ă)',
};

/** CIVIL_STATUS_LABELS plus the "nothing recorded" bucket, for the roster
 *  filter and the Evidențe chart — both of which have to name the blanks. */
export const CIVIL_STATUS_LABELS_ALL: Record<string, string> = {
  ...CIVIL_STATUS_LABELS,
  [CIVIL_STATUS_UNSPECIFIED]: 'Nespecificat',
};

/* --- Group Category --- */
export const GROUP_CATEGORY_LABELS: Record<GroupCategory, string> = {
  [GroupCategory.SECTOR]: 'Sector',
  [GroupCategory.TINERI]: 'Tineri',
  [GroupCategory.MUZICAL]: 'Muzical',
  [GroupCategory.SBC]: 'Școala Biblică',
  [GroupCategory.ALTELE]: 'Altele',
};

export const GROUP_CATEGORY_COLORS: Record<GroupCategory, string> = {
  [GroupCategory.SECTOR]: '#5e81f4',
  [GroupCategory.TINERI]: '#7ce7ac',
  [GroupCategory.MUZICAL]: '#f4be5e',
  [GroupCategory.SBC]: '#9698d6',
  [GroupCategory.ALTELE]: '#8181a5',
};

/* --- Visit Type --- */
export const VISIT_TYPE_LABELS: Record<VisitType, string> = {
  pastorala: 'Pastorală',
  sectoriala: 'Sectorială',
  nevoie_speciala: 'Nevoie Specială',
  incurajare: 'Încurajare',
  ajutoare: 'Ajutoare',
};

export const VISIT_TYPE_SHORT_LABELS: Record<VisitType, string> = {
  pastorala: 'VP',
  sectoriala: 'VS',
  nevoie_speciala: 'NS',
  incurajare: 'Î',
  ajutoare: 'A',
};

export const VISIT_TYPE_COLORS: Record<VisitType, string> = {
  pastorala: '#5e81f4',
  sectoriala: '#7c3aed',
  nevoie_speciala: '#dc2626',
  incurajare: '#059669',
  ajutoare: '#d97706',
};

/* --- Need Category --- */
export const NEED_CATEGORY_LABELS: Record<NeedCategory, string> = {
  medicala: 'Medicală',
  financiara: 'Financiară',
  materiala: 'Materială',
  spirituala: 'Spirituală',
  sociala: 'Socială',
  altele: 'Altele',
};

export const NEED_CATEGORY_COLORS: Record<NeedCategory, string> = {
  medicala: '#dc2626',
  financiara: '#d97706',
  materiala: '#0891b2',
  spirituala: '#7c3aed',
  sociala: '#059669',
  altele: '#64748b',
};

/* --- Need Status --- */
export const NEED_STATUS_LABELS: Record<NeedStatus, string> = {
  deschisa: 'Deschisă',
  in_lucru: 'În Lucru',
  rezolvata: 'Rezolvată',
  anulata: 'Anulată',
};

export const NEED_STATUS_COLORS: Record<NeedStatus, string> = {
  deschisa: '#dc2626',
  in_lucru: '#d97706',
  rezolvata: '#059669',
  anulata: '#94a3b8',
};

/* --- Need Priority --- */
export const NEED_PRIORITY_LABELS: Record<NeedPriority, string> = {
  urgenta: 'Urgentă',
  normala: 'Normală',
  scazuta: 'Scăzută',
};

export const NEED_PRIORITY_COLORS: Record<NeedPriority, string> = {
  urgenta: '#dc2626',
  normala: '#475569',
  scazuta: '#94a3b8',
};

/* --- Audit Actions --- */
export const AUDIT_ACTION_LABELS: Record<string, string> = {
  // Data operations
  created: 'Creare',
  updated: 'Actualizare',
  deleted: 'Ștergere',
  restored: 'Restaurare',
  bulk_updated: 'Actualizare în masă',
  viewed: 'Vizualizare',

  // Auth
  login: 'Autentificare',
  login_failed: 'Autentificare eșuată',
  login_blocked: 'Autentificare blocată',
  logout: 'Deconectare',

  // Password
  password_reset: 'Resetare parolă (admin)',
  password_reset_requested: 'Cerere resetare parolă',
  password_reset_completed: 'Parolă resetată',
  password_reset_failed: 'Resetare parolă eșuată',

  // User management
  user_activated: 'Cont activat',
  user_deactivated: 'Cont dezactivat',
  roles_updated: 'Roluri actualizate',
  scopes_updated: 'Grupuri atribuite',
  status_changed: 'Schimbare status',

  // Group operations
  member_added: 'Membru adăugat',
  member_removed: 'Membru eliminat',
  member_role_updated: 'Rol grup actualizat',
  members_batch_added: 'Membri adăugați în masă',

  // Documents
  document_generated: 'Document generat',
  export: 'Export',
};

/* --- Audit Categories --- */
export const AUDIT_CATEGORY_LABELS: Record<string, string> = {
  auth: 'Autentificare',
  data: 'Date',
  security: 'Securitate',
  system: 'Sistem',
};

export const AUDIT_CATEGORY_COLORS: Record<string, string> = {
  auth: '#0891b2',
  data: '#4f46e5',
  security: '#dc2626',
  system: '#6b7280',
};

/* --- Family Role --- */
export const FAMILY_ROLE_LABELS: Record<string, string> = {
  head: 'Cap Familie',
  spouse: 'Soț/Soție',
  child: 'Copil',
  other: 'Altă Rudă',
};

/* --- Boolean Label Flags --- */
/** Used by the filter checkboxes, where the two causes stay separate on purpose. */
export const LABEL_FLAG_LABELS: Record<string, string> = {
  is_prodigal: 'Fiu Risipitor',
  is_unbaptized_adult: 'Adult Nebotezat',
  has_disability: 'Dizabilitate',
  has_special_need: 'Nevoie Specială',
  no_membership: 'Fără Membralitate',
};

/**
 * Church vocabulary: turning 18 unbaptized moves you into "fii risipitori"
 * automatically, so everyone on the pastoral list carries this one label —
 * there is no second category name in the UI.
 */
export const PASTORAL_CASE_LABEL = 'Fiu Risipitor';

/** True when the person belongs on the pastoral list, for either cause. */
export function isPastoralCase(m: { is_prodigal?: boolean; is_unbaptized_adult?: boolean }): boolean {
  return !!(m.is_prodigal || m.is_unbaptized_adult);
}

/**
 * Why they are on the list, as a quiet qualifier next to the label. The cause
 * is never promoted to a category of its own, but it is not thrown away
 * either: "18+ nebotezat" is derived and clears itself once a baptism is
 * recorded, while a manual flag only clears when someone unticks it.
 */
export function pastoralCauseHint(m: { is_prodigal?: boolean; is_unbaptized_adult?: boolean }): string | null {
  if (m.is_unbaptized_adult && m.is_prodigal) return 'bifat · 18+ nebotezat';
  if (m.is_unbaptized_adult) return 'automat · 18+ nebotezat';
  if (m.is_prodigal) return 'bifat manual';
  return null;
}

/* --- User Roles --- */
export const USER_ROLE_LABELS: Record<string, string> = {
  admin: 'Administrator',
  pastor: 'Pastor',
  secretary: 'Secretar',
  data_entry: 'Operator date',
  spiritual_editor: 'Editor spiritual',
  sector_leader: 'Responsabil sector',
  group_leader: 'Lider Grup',
  group_assistant: 'Asistent Grup',
  observer: 'Observator',
  viewer: 'Vizualizator',
  communion_recorder: 'Înregistrator Cină',
  kindergarten_admin: 'Administrator grădiniță',
  kindergarten_teacher: 'Educatoare',
};

// ─── Grădinița Gosen ───

export const KG_LEVEL_LABELS: Record<string, string> = {
  mica: 'Grupa mică',
  mijlocie: 'Grupa mijlocie',
  mare: 'Grupa mare',
};

export const KG_ENROLLMENT_STATUS_LABELS: Record<string, string> = {
  active: 'Înscris',
  withdrawn: 'Retras',
  graduated: 'Absolvent',
};

export const KG_FEE_RULE_LABELS: Record<string, string> = {
  standard: 'Contribuție standard',
  church_covered: 'Acoperit de biserică',
  second_child_discount: 'Al doilea copil (redus)',
  custom: 'Sumă personalizată',
};

export const KG_RELATIONSHIP_LABELS: Record<string, string> = {
  mama: 'Mamă',
  tata: 'Tată',
  tutore: 'Tutore',
  parinte: 'Părinte',
};

export const KG_MEMBERSHIP_SOURCE_LABELS: Record<string, string> = {
  override: 'Setat manual',
  linked: 'Din registrul Betel',
  none: 'Fără legătură cu registrul',
};

export const KG_PAYMENT_METHOD_LABELS: Record<string, string> = {
  cash_receipt: 'Chitanță (numerar)',
  bank_transfer: 'Ordin de plată (transfer)',
  other: 'Altă metodă',
};

export const KG_PAYMENT_METHOD_SHORT: Record<string, string> = {
  cash_receipt: 'Chitanță',
  bank_transfer: 'OP',
  other: 'Altele',
};

export const KG_CELL_STATE_LABELS: Record<string, string> = {
  paid: 'Achitat',
  partial: 'Parțial',
  due: 'Restant',
  future: 'Urmează',
  church: 'Biserică',
  waived: 'Scutit',
  free: 'Fără contribuție',
  not_enrolled: 'Neînscris',
};

export const KG_MONTH_LABELS: Record<number, string> = {
  1: 'Ian', 2: 'Feb', 3: 'Mar', 4: 'Apr', 5: 'Mai', 6: 'Iun',
  7: 'Iul', 8: 'Aug', 9: 'Sep', 10: 'Oct', 11: 'Nov', 12: 'Dec',
};

